<template>
  <form class="flex flex-no-wrap text-sm add-variant sm:text-base">
    <number-input
      v-model="model.quantity"
      :min="1"
      :max="maxQuantity"
      :disabled="soldOut"
      label="Quantity"
      class="flex-auto add-variant__quantity"
    />
    <button
      class="whitespace-no-wrap add-variant__add flex-0"
      fill
      @click.stop.prevent="debouncedSubmit"
    >
      {{ soldOut ? 'Sold out' : 'Add to cart' }}
    </button>
  </form>
</template>

<script>
import debounce from 'lodash/debounce'
import { mapState, mapGetters, mapActions } from 'vuex'
import extractShopifyId from '~/util/shopify/extract-shopify-id'
import formatLineItemSchema from '~/util/shopify/format-line-item-schema'
import formatLineItemAttributes from '~/util/shopify/format-line-item-attributes'
import getMaxVariantQuantity from '~/util/shopify/get-max-variant-quantity'
import NumberInput from '~/components/form/number-input.vue'

export default {
  name: 'AddVariantToCart',

  components: {
    NumberInput
  },

  props: {
    product: {
      default: () => {},
      type: Object
    },
    variant: {
      default: () => {},
      type: Object
    },
    isSubscription: {
      default: false,
      type: Boolean
    },
    interval: {
      default: '2 weeks',
      type: String
    }
  },

  data() {
    return {
      model: {
        quantity: 1
      },
      isSubmitting: false
    }
  },

  computed: {
    ...mapState({
      googleClientId: state => state.googleClientId,
      cartId: state => state.storage.cartId
    }),
    ...mapGetters({
      bp: 'screen/breakpoint',
      productsByCategory: 'shop/productsByCategory',
      subscriptionById: 'shop/subscriptionById'
    }),
    subscription() {
      const shopify = this.product.shopify
      if (!shopify || !shopify.subscription) {
        return undefined
      }
      return this.subscriptionById(shopify.subscription.id)
    },
    /**
     * Returns a subscription variant with sku matching
     * the product's sku (if one exists)
     * @function
     */
    subscriptionVariant() {
      const subscription = this.subscription
      const productVariant = this.variant
      let variant
      if (subscription && productVariant) {
        variant = subscription.variants.edges.find(({ node }) => {
          return node.sku === productVariant.sku
        })
        if (variant) {
          variant = variant.node
        }
      }
      return variant
    },
    price() {
      return this.variant.price?.amount
    },
    soldOut() {
      const variant = this.variant
      return !variant || !variant.availableForSale
    },
    maxQuantity() {
      const variant = this.variant
      if (!variant) {
        return undefined
      }
      const available = variant.availableForSale
      let quantity = variant.quantityAvailable
      if (available && quantity <= 0) {
        quantity = 9999
      } else if (!available) {
        quantity = 0
      }
      return quantity
    }
  },

  created() {
    this.debouncedSubmit = debounce(
      this.submit,
      process.env.shopifyRequestDebounce
    )
  },

  mounted() {
    this.$nextTick(() => {
      const product = this.product
      const variant = this.variant
      if (!product || !variant) {
        return
      }
      this.$gtm.push({
        event: 'Product Viewed',
        google_client_id: this.googleClientId,
        product_id: product.id,
        sku: variant.sku,
        name: product.title,
        variant: variant.title,
        price: variant.price?.amount,
        quantity: this.model.quantity,
        value: variant.price?.amount,
        url: `${process.env.canonicalUrl}${this.$route.path}`
      })
    })
  },

  methods: {
    ...mapActions({
      addToCart: 'shop/addToCart'
    }),
    newMessage(message) {
      if (this.message) {
        this.message.goAway()
      }
      this.message = this.$toast.global.cartInfo({ message })
    },
    /**
     * Formats and adds the current product or subscription
     * variant to the user's checkout as a line item based on
     * the current product type
     * @function
     */
    async submit() {
      if (this.isSubmitting) {
        return
      }
      this.isSubmitting = true

      const product = this.product
      const subscription = this.subscription
      const productVariant = this.variant
      const subscriptionVariant = this.subscriptionVariant
      const isSubscription = this.isSubscription
      const selectedProduct =
        isSubscription && subscriptionVariant ? subscription : product
      const variant =
        isSubscription && subscriptionVariant
          ? subscriptionVariant
          : productVariant
      const quantity = this.model.quantity
      const soldOut = this.soldOut
      const maxVariantQuantity = getMaxVariantQuantity(variant, quantity)
      const maxQuantity =
        maxVariantQuantity <= 0 && !soldOut ? quantity : maxVariantQuantity
      if (quantity !== maxQuantity) {
        this.newMessage(
          'We updated your cart with the last remaining items we have in stock'
        )
      }

      const sellingPlan =
        this.interval &&
        subscription?.sellingPlans?.find(({ name }) =>
          name.toLowerCase().includes(this.interval.toLowerCase())
        )

      const attributes = formatLineItemAttributes({
        isRebuyProductDiscovery: this.rebuyTracker === product.id
      })

      const lineItem = formatLineItemSchema(
        {
          ...selectedProduct,
          variant,
          attributes,
          productType: product.category,
          sellingPlan
        },
        'product'
      )
      lineItem.node.quantity = maxQuantity

      await this.addToCart({ edge: lineItem, additive: true })

      this.trackAdded({
        cart: this.cartId,
        id: product.id,
        decodedId: extractShopifyId(product?.shopify?.product?.id),
        category: this.category,
        sku: variant.sku,
        name: product.title,
        variant: variant.title,
        price: variant.price?.amount,
        quantity: maxQuantity,
        url: `${process.env.canonicalUrl}${this.$route.path}`,
        image: variant.image?.transformedSrc,
        discount: subscriptionVariant
          ? subscriptionVariant.price?.amount
          : undefined
      })

      this.isSubmitting = false
    },
    trackAdded(added) {
      this.$gtm.push({
        event: 'Add To Cart',
        google_client_id: this.googleClientId,
        cart_id: added.cartId,
        product_id: added.id,
        product_id_shopify: added.decodedId,
        sku: added.sku,
        name: added.name,
        variant: added.variant,
        price: added.price?.amount,
        quantity: added.quantity,
        url: added.url
      })
      // klaviyo
      var klaviyo = window.klaviyo || [] // eslint-disable-line
      klaviyo.push([
        'track',
        'Added to Cart',
        {
          Brand: 'Recess',
          Categories: [added.category],
          CompareAtPrice: added.discount,
          ImageURL: added.image,
          Name: added.name,
          Price: added.price?.amount,
          ProductID: added.id,
          URL: added.url
        }
      ])
    }
  }
}
</script>

<style>
.add-variant {
  overflow: hidden;
  text-transform: lowercase;
  width: 100%;
}

.add-variant .add-variant__quantity,
.add-variant .add-variant__add {
  border: 1px solid currentColor;
  flex: 1 1 50% !important;
  margin: 0;
  max-width: 50%;
}

.add-variant .add-variant__quantity .number__ui,
.add-variant .add-variant__add {
  padding: 0.25em;
}

.add-variant .add-variant__quantity label {
  @apply sr-only;
  padding: 0;
}

.add-variant .add-variant__quantity .number__ui {
  border: 0;
  border-radius: 0;
  height: 100%;
  width: 100%;
}

.add-variant__add {
  background-color: theme('colors.peach');
  font-weight: theme('fontWeight.bold');
  transition: background-color var(--hover-speed);
}

.add-variant .number__ui:hover,
[data-js-focus-visible] .add-variant .number__ui[data-focus-visible-added],
.add-variant__add:hover,
[data-js-focus-visible] .add-variant__add[data-focus-visible-added] {
  background-color: theme('colors.white');
}
</style>
